import type { RefAttributes } from "react";
import {
  calendarClock,
  chartMixedUpCircleDollar,
  circleDollar,
} from "@simplybusiness/icons";
import { Box, Flex, Icon, Text, Segment } from "@simplybusiness/mobius";
import type { PaymentInformationProps } from ".";
import { QuoteCardBadge, QuoteCardBadges } from "../../../QuoteCardBadges";
import { ProductInfo } from "../../../../../shared/ProductInfo";
import { transformCoverage } from "../../../Coverage/transformCoverage";

export const PaymentInformation = ({
  ref,
  ...props
}: PaymentInformationProps & RefAttributes<HTMLDivElement>) => {
  const {
    quote: {
      installmentsAvailable,
      downpaymentAmount,
      installmentsAmount,
      installmentsCount,
      installmentsTotal,
      premium,
      processingFee,
      covers,
      multiProductPremiums,
      insurerLogo,
      policyStartDate,
      externalPaymentOptions,
      primaryProductTitle,
      installmentFeesApplied,
    },
    paymentPeriod = "monthly",
  } = props;
  const isMonthly = paymentPeriod === "monthly";
  const hasInstallments = installmentsAvailable && installmentsAmount;
  const showZeroInterestBadge =
    hasInstallments && isMonthly && !externalPaymentOptions;
  const premiumEqualsInstallments =
    !externalPaymentOptions ||
    (externalPaymentOptions && premium === installmentsTotal);
  const showTotalCostBadge =
    hasInstallments &&
    !isMonthly &&
    premiumEqualsInstallments &&
    !installmentFeesApplied;
  const productTitle =
    primaryProductTitle || transformCoverage(covers)[0]?.displayName;
  const totalPrice = isMonthly ? installmentsTotal : premium;
  const processingFeeDetailsText = processingFee
    ? `(inc. tax + ${processingFee} processing fee)`
    : "(inc. tax + fees)";

  return (
    <Box
      className="mobius-payment-info --has-divider"
      ref={ref}
      data-testid="payment-information"
    >
      <Text className="mobius-payment-info__title" variant="h3">
        Payment Information
      </Text>
      <QuoteCardBadges>
        <QuoteCardBadge
          icon={
            <Icon icon={calendarClock} title="Policy duration information" />
          }
          data-testid="quote-card-badge-12-month-policy"
        >
          12 month policy
        </QuoteCardBadge>
        {showZeroInterestBadge && (
          <QuoteCardBadge
            icon={
              <Icon
                icon={chartMixedUpCircleDollar}
                title="Zero interest payment option"
              />
            }
            data-testid="quote-card-badge-zero-interest"
          >
            Zero interest
          </QuoteCardBadge>
        )}
        {showTotalCostBadge && (
          <QuoteCardBadge
            icon={
              <Icon icon={circleDollar} title="Cost comparison information" />
            }
            data-testid="quote-card-badge-annual-cost-same-as-monthly"
          >
            Total annual cost same as monthly
          </QuoteCardBadge>
        )}
      </QuoteCardBadges>
      {multiProductPremiums ? (
        <>
          <ProductInfo
            logo={insurerLogo}
            productTitle="General Liability"
            premium={multiProductPremiums?.generalLiability}
            policyStartDate={policyStartDate}
          />
          <ProductInfo
            logo={insurerLogo}
            productTitle="Professional Liability"
            premium={multiProductPremiums?.professionalLiability}
            policyStartDate={policyStartDate}
          />
        </>
      ) : (
        <ProductInfo
          logo={insurerLogo}
          productTitle={productTitle}
          premium={totalPrice}
          policyStartDate={policyStartDate}
        />
      )}
      <Flex flexDirection="column">
        <div className="mobius-payment-info__total-outer-container">
          <>
            <Flex
              justifyContent="space-between"
              gap="xs"
              className="mobius-payment-info__total-today"
              flexGrow={1}
            >
              <span>To pay today</span>
              <span>
                {isMonthly && hasInstallments ? downpaymentAmount : premium}
              </span>
            </Flex>
            {isMonthly && hasInstallments && (
              <Flex
                justifyContent="space-between"
                gap="xs"
                className="mobius-payment-info__installments"
                flexGrow={1}
              >
                <span>{installmentsCount} monthly payments of</span>
                <span>{installmentsAmount}</span>
              </Flex>
            )}
          </>
          <Segment colour="subtle" className="mobius-payment-info__total">
            <Flex justifyContent="space-between" gap="xs" flexGrow={1}>
              <span>
                <strong>Total</strong> {processingFeeDetailsText}
              </span>
              <span>
                <strong data-testid="payment-information-total">
                  {isMonthly && installmentsTotal ? installmentsTotal : premium}
                </strong>
              </span>
            </Flex>
          </Segment>
        </div>
      </Flex>
    </Box>
  );
};

PaymentInformation.displayName = "PaymentInformation";
